import React, { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  useMediaQuery,
  Button,
  Card,
  Grid,
  CardContent,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import CustomTabs from "../UI/reusable-components/CustomTabs";
import Indicator from "../UI/reusable-components/Indicator";
import { useDispatch, useSelector } from "react-redux";
import { getMarketPlaces } from "../../redux/actions/marketplacesActions";
import { getEcommercePlaces } from "../../redux/actions/ecommercePlaceActions";
import { fetchOrders } from "../../redux/actions/ordersActions";
import { OrderStats } from "../UI/reusable-components/OrderComponents/OrderStats";
import AddIcon from "@mui/icons-material/Add";
import InfoIcon from "@mui/icons-material/Info";

const Dashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (newValue === 0) navigate("/");
    if (newValue === 1) navigate("/analytics");
    if (newValue === 2) navigate("/tasks");
  };

  // Responsive breakpoints
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery((theme) =>
    theme.breakpoints.between("sm", "md")
  );
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up("md"));

  const { orders, totalOrders } = useSelector((state) => state.orders);
  const authUserId = useSelector((state) => state?.auth?.user?.id);
  const [marketplaces, setMarketplaces] = useState([]);
  const [ecommerce, setEcommerce] = useState([]);
  const [loading, setLoading] = useState(false);
  const token = useSelector((state) => state?.auth?.user?.accessToken?.token);
  const dispatch = useDispatch();
  const getMarketPlace = async () => {
    try {
      setLoading(true);
      const response = await dispatch(getMarketPlaces(token));
      if (response.success) {
        setMarketplaces(response?.data);
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.log("Marketler getirilemedi", err);
    }
  };
  const [pageSize, setPageSize] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const getEcommercePlace = async (token) => {
    try {
      const response = await dispatch(getEcommercePlaces(token));
      if (response.success) {
        setEcommerce(response?.data);
      }
    } catch (err) {
      console.log("Marketler getirilemedi", err);
    }
  };

  useEffect(() => {
    getMarketPlace();
    getEcommercePlace();
    if (authUserId) {
      try {
        const dateFilter = true;
        dispatch(fetchOrders(authUserId, currentPage, pageSize, dateFilter));
      } catch {}
    }
  }, []);
  const handleOrder = () => {
    navigate("/orders");
  };

  const MarketplaceCard = ({ item, showIcon = true, type }) => {
    const matchingOrders = orders.filter((order) =>
      type === "marketpleace"
        ? order.marketPlaceId === item.id
        : order.ecommercePlaceId === item.id
    );

    const hasOrders = matchingOrders.length > 0;
    const last24HoursOrders = matchingOrders.filter((order) => {
      const orderDate = new Date(order.createdAt);
      const now = new Date();
      const diffHours = (now - orderDate) / (1000 * 60 * 60);
      return diffHours <= 24;
    });

    // Son 1 saatteki siparişleri hesapla
    const lastHourOrders = matchingOrders.filter((order) => {
      const orderDate = new Date(order.createdAt);
      const now = new Date();
      const diffMinutes = (now - orderDate) / (1000 * 60);
      return diffMinutes <= 60;
    });

    return (
      <Card
        onClick={() =>
          navigate("/orders", {
            state: { filterType: type, filterId: item.id },
          })
        }
        sx={{
          height: { xs: 160, sm: 170, md: 180 },
          display: "flex",
          flexDirection: "column",
          background: `linear-gradient(165deg, white, ${item.colorCode}08)`,
          backdropFilter: "blur(10px)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          border: `1px solid ${item.colorCode}15`,
          position: "relative",
          overflow: "hidden",
          cursor: "pointer",
          borderRadius: { xs: "12px", md: "16px" },
          "&:hover": {
            transform: { xs: "translateY(-4px)", md: "translateY(-6px)" },
            boxShadow: `0 12px 20px ${item.colorCode}15`,
            "& .platform-icon": {
              transform: hasOrders ? "scale(1.08)" : "scale(1.05)",
            },
          },
        }}
      >
        {/* Aktif Sipariş İndikatörü */}
        {lastHourOrders.length > 0 && (
          <Box
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              width: 8,
              height: 8,
              backgroundColor: "#10B981",
              borderRadius: "50%",
              boxShadow: "0 0 0 4px rgba(16, 185, 129, 0.2)",
              animation: "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
            }}
          />
        )}

        {/* Sipariş Sayısı */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            left: 0,
            background: `linear-gradient(to right, ${item.colorCode}10, ${item.colorCode}18)`,
            backdropFilter: "blur(8px)",
            padding: { xs: "8px 12px", md: "12px 16px" },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: { xs: 1, md: 1.5 },
            borderBottom: `1px solid ${item.colorCode}10`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, md: 1.5 },
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: "0.85rem", sm: "0.9rem", md: "0.95rem" },
                color: item.colorCode,
                display: "flex",
                alignItems: "center",
                gap: { xs: 0.5, md: 1 },
                fontWeight: 600,
              }}
            >
              <Box
                component="span"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "1rem", sm: "1.05rem", md: "1.1rem" },
                  background: `linear-gradient(135deg, ${item.colorCode}, ${item.colorCode}cc)`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {matchingOrders.length}
              </Box>
              Yeni Sipariş
            </Typography>

            {last24HoursOrders.length > 0 && (
              <Box
                sx={{
                  px: { xs: 0.75, md: 1 },
                  py: { xs: 0.2, md: 0.3 },
                  borderRadius: { xs: "8px", md: "12px" },
                  fontSize: { xs: "0.7rem", md: "0.75rem" },
                  background: `linear-gradient(135deg, ${item.colorCode}12, ${item.colorCode}20)`,
                  color: item.colorCode,
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: 0.3, md: 0.5 },
                  border: `1px solid ${item.colorCode}15`,
                }}
              >
                <Box
                  component="span"
                  sx={{
                    fontSize: { xs: "0.65rem", md: "0.7rem" },
                    opacity: 0.9,
                  }}
                >
                  ⚡
                </Box>
                {last24HoursOrders.length}
              </Box>
            )}
          </Box>
        </Box>

        {/* Platform İkonu */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pt: { xs: 5, md: 6 },
            pb: { xs: 6, md: 8 },
          }}
        >
          <Box
            className="platform-icon"
            sx={{
              position: "relative",
              transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              filter: hasOrders ? "none" : "grayscale(0.4)",
              opacity: hasOrders ? 1 : 0.7,
              mt: { xs: 3, md: 4 },
              mb: { xs: 6, md: 8 },
              "&::before": {
                content: '""',
                position: "absolute",
                inset: { xs: -8, md: -12 },
                border: `2px solid ${item.colorCode}20`,
                borderRadius: { xs: "16px", md: "22px" },
                animation: hasOrders ? "borderPulse 3s infinite" : "none",
                opacity: hasOrders ? 0.8 : 0.3,
              },
              "@keyframes borderPulse": {
                "0%, 100%": {
                  transform: "scale(0.95)",
                  opacity: 0.4,
                },
                "50%": {
                  transform: "scale(1.02)",
                  opacity: 0.8,
                },
              },
            }}
          >
            {showIcon ? (
              <Box
                component="img"
                src={item.iconLink}
                alt={item.name}
                referrerPolicy="no-referrer"
                sx={{
                  width: { xs: 60, sm: 65, md: 75 },
                  height: { xs: 60, sm: 65, md: 75 },
                  borderRadius: { xs: "12px", md: "16px" },
                  p: { xs: 1, md: 1.5 },
                  bgcolor: "white",
                  boxShadow: hasOrders
                    ? `0 8px 24px ${item.colorCode}25`
                    : "0 4px 12px rgba(0,0,0,0.08)",
                  transition: "all 0.3s ease",
                }}
              />
            ) : (
              <Box
                sx={{
                  width: { xs: 60, sm: 65, md: 75 },
                  height: { xs: 60, sm: 65, md: 75 },
                  borderRadius: { xs: "12px", md: "16px" },
                  background: hasOrders
                    ? `linear-gradient(135deg, ${item.colorCode}, ${item.colorCode}dd)`
                    : `linear-gradient(135deg, #94A3B8, #CBD5E1)`,
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontWeight: "bold",
                  boxShadow: hasOrders
                    ? `0 8px 24px ${item.colorCode}25`
                    : "0 4px 12px rgba(0,0,0,0.08)",
                }}
              >
                {item.name[0]}
              </Box>
            )}
          </Box>
        </Box>
      </Card>
    );
  };

  // Platform tipine göre toplam sipariş ve gelir hesaplama
  const calculateStats = (platforms, type) => {
    return platforms.reduce(
      (acc, platform) => {
        const platformOrders = orders.filter((order) =>
          type === "marketpleace"
            ? order.marketPlaceId === platform.id
            : order.ecommercePlaceId === platform.id
        );

        const totalRevenue = platformOrders.reduce(
          (sum, order) => sum + order.totalAmount,
          0
        );

        return {
          orders: acc.orders + platformOrders.length,
          revenue: acc.revenue + totalRevenue,
        };
      },
      { orders: 0, revenue: 0 }
    );
  };

  const marketplaceStats = calculateStats(marketplaces, "marketpleace");
  const ecommerceStats = calculateStats(ecommerce, "ecommerce");

  const StatCard = ({ title, value, icon, color }) => (
    <Box
      sx={{
        p: { xs: 2, sm: 2.5, md: 3 },
        height: { xs: 100, sm: 110, md: 120 },
        borderRadius: { xs: 2, md: 3 },
        background: "white",
        boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: { xs: 2, md: 3 },
        transition: "all 0.3s ease",
        "&:hover": {
          transform: { xs: "translateY(-3px)", md: "translateY(-5px)" },
          boxShadow: "0 8px 25px rgba(0,0,0,0.08)",
        },
      }}
    >
      <Box
        sx={{
          width: { xs: 45, sm: 50, md: 56 },
          height: { xs: 45, sm: 50, md: 56 },
          borderRadius: { xs: "12px", md: "16px" },
          background: `${color}12`,
          color: color,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: { xs: "22px", sm: "24px", md: "28px" },
          flexShrink: 0,
          transition: "all 0.3s ease",
          "&:hover": {
            transform: "scale(1.1)",
            background: `${color}18`,
          },
        }}
      >
        {icon}
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          flex: 1,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            mb: { xs: 0.3, md: 0.5 },
            fontSize: { xs: "0.85rem", sm: "0.9rem", md: "0.95rem" },
            textAlign: "center",
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: "#2f2f2f",
            letterSpacing: "-0.5px",
            textAlign: "center",
            fontSize: { xs: "1.1rem", sm: "1.2rem", md: "1.25rem" },
          }}
        >
          {value.toLocaleString("tr-TR")}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <>
      {loading ? (
        <Box
          sx={{
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            maxWidth: 1400,
            mx: "auto",
            px: {
              xs: 1.5, // Mobilde daha az padding
              sm: 2.5, // Tablet
              md: 4, // Desktop
            },
            py: { xs: 2, sm: 3, md: 4 },
            width: "100%",
            pt: { xs: 7, sm: 3, md: 4 }, // Masaüstü için padding-top değerini düzelttim
          }}
        >
          <Box sx={{ mb: { xs: 3, sm: 4, md: 5 } }}>
            <Typography
              variant="h4"
              sx={{
                mb: { xs: 2, sm: 2.5, md: 3 },
                fontWeight: 700,
                color: "#1a1a1a",
                letterSpacing: "-0.5px",
                fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
              }}
            >
              Genel Bakış
            </Typography>
            <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Toplam Yeni Sipariş"
                  value={marketplaceStats.orders + ecommerceStats.orders}
                  icon="📦"
                  color="#0066FF"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Yeni Pazaryeri Siparişleri"
                  value={marketplaceStats.orders}
                  icon="🏪"
                  color="#00B795"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Yeni E-ticaret Siparişleri"
                  value={ecommerceStats.orders}
                  icon="🛍️"
                  color="#FF4757"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  title="Aktif Platformlar"
                  value={marketplaces.length + ecommerce.length}
                  icon="⚡"
                  color="#6C5CE7"
                />
              </Grid>
            </Grid>
          </Box>

          <OrderStats />

          <Box sx={{ mb: { xs: 3, sm: 3.5, md: 4 } }}>
            <Typography
              variant="h5"
              sx={{
                mb: { xs: 2, sm: 2.5, md: 3 },
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                gap: { xs: 1.5, md: 2 },
                color: "#1a1a1a",
                fontSize: { xs: "1.25rem", sm: "1.35rem", md: "1.5rem" },
                flexWrap: { xs: "wrap", sm: "nowrap" },
                "& .platform-count": {
                  fontSize: { xs: "0.8rem", md: "0.9rem" },
                  fontWeight: 600,
                  py: { xs: 0.5, md: 0.75 },
                  px: { xs: 1.5, md: 2 },
                  borderRadius: 2,
                  letterSpacing: "0.3px",
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ color: "#2E5CFF", fontSize: "1.5rem" }}>🏪</Box>
                Pazaryeri Platformları
              </Box>
              <Box
                className="platform-count"
                sx={{
                  backgroundColor: "#2E5CFF12",
                  color: "#2E5CFF",
                }}
              >
                {marketplaces.length} Platform
              </Box>
              <Tooltip title="Yeni Pazaryeri Ekle" placement="right" arrow>
                <IconButton
                  onClick={() => navigate("/Application")}
                  sx={{
                    backgroundColor: "#2E5CFF12",
                    color: "#2E5CFF",
                    "&:hover": {
                      backgroundColor: "#2E5CFF22",
                    },
                    width: { xs: 30, md: 35 },
                    height: { xs: 30, md: 35 },
                  }}
                >
                  <AddIcon sx={{ fontSize: { xs: "1.1rem", md: "1.25rem" } }} />
                </IconButton>
              </Tooltip>
            </Typography>
            {marketplaces.length > 0 ? (
              <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
                {marketplaces.map((market) => (
                  <Grid item xs={12} sm={6} md={3} key={market.id}>
                    <MarketplaceCard
                      item={market}
                      showIcon={true}
                      type="marketpleace"
                    />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <div
                style={{
                  backgroundColor: "#F5F7FF",
                  padding: { xs: "12px", md: "16px" },
                  borderRadius: { xs: "6px", md: "8px" },
                  marginBottom: { xs: "16px", md: "20px" },
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: { xs: "12px", md: "16px" },
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <InfoIcon style={{ color: "#3366FF" }} />
                  <span style={{ color: "#3366FF" }}>
                    Henüz hiç pazaryeri eklenmemiş. Yeni bir pazaryeri eklemek
                    için aşağıdaki butona tıklayabilirsiniz.
                  </span>
                </div>
                <Button
                  variant="contained"
                  onClick={() => navigate("/Application")}
                  startIcon={
                    <AddIcon
                      sx={{ fontSize: { xs: "1.1rem", md: "1.25rem" } }}
                    />
                  }
                  sx={{
                    backgroundColor: "#3366FF",
                    color: "white",
                    textTransform: "none",
                    borderRadius: "20px",
                    padding: { xs: "6px 20px", md: "8px 24px" },
                    fontSize: { xs: "0.9rem", md: "1rem" },
                  }}
                >
                  Yeni Pazaryeri Ekle
                </Button>
              </div>
            )}
          </Box>

          <Box>
            <Typography
              variant="h5"
              sx={{
                mb: { xs: 2, sm: 2.5, md: 3 },
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                gap: { xs: 1.5, md: 2 },
                color: "#1a1a1a",
                fontSize: { xs: "1.25rem", sm: "1.35rem", md: "1.5rem" },
                flexWrap: { xs: "wrap", sm: "nowrap" },
                "& .platform-count": {
                  fontSize: { xs: "0.8rem", md: "0.9rem" },
                  fontWeight: 600,
                  py: { xs: 0.5, md: 0.75 },
                  px: { xs: 1.5, md: 2 },
                  borderRadius: 2,
                  letterSpacing: "0.3px",
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ color: "#3366FF", fontSize: "1.5rem" }}>🛍️</Box>
                E-ticaret Platformları
              </Box>
              <Box
                className="platform-count"
                sx={{
                  backgroundColor: "#3366FF12",
                  color: "#3366FF",
                }}
              >
                {ecommerce.length} Platform
              </Box>
              <Tooltip title="Yeni E-Ticaret Ekle" placement="right" arrow>
                <IconButton
                  onClick={() => navigate("/Application")}
                  sx={{
                    backgroundColor: "#3366FF12",
                    color: "#3366FF",
                    "&:hover": {
                      backgroundColor: "#3366FF22",
                    },
                    width: { xs: 30, md: 35 },
                    height: { xs: 30, md: 35 },
                  }}
                >
                  <AddIcon sx={{ fontSize: { xs: "1.1rem", md: "1.25rem" } }} />
                </IconButton>
              </Tooltip>
            </Typography>
            {ecommerce.length > 0 ? (
              <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
                {ecommerce.map((platform) => (
                  <Grid item xs={12} sm={6} md={3} key={platform.id}>
                    <MarketplaceCard
                      item={platform}
                      showIcon={true}
                      type="ecommerce"
                    />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <div
                style={{
                  backgroundColor: "#F5F7FF",
                  padding: { xs: "12px", md: "16px" },
                  borderRadius: { xs: "6px", md: "8px" },
                  marginBottom: { xs: "16px", md: "20px" },
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: { xs: "12px", md: "16px" },
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <InfoIcon style={{ color: "#3366FF" }} />
                  <span style={{ color: "#3366FF" }}>
                    Henüz hiç e-ticaret platformu eklenmemiş. Yeni bir e-ticaret
                    platformu eklemek için aşağıdaki butona tıklayabilirsiniz.
                  </span>
                </div>
                <Button
                  variant="contained"
                  onClick={() => navigate("/Application")}
                  startIcon={
                    <AddIcon
                      sx={{ fontSize: { xs: "1.1rem", md: "1.25rem" } }}
                    />
                  }
                  sx={{
                    backgroundColor: "#3366FF",
                    color: "white",
                    textTransform: "none",
                    borderRadius: "20px",
                    padding: { xs: "6px 20px", md: "8px 24px" },
                    fontSize: { xs: "0.9rem", md: "1rem" },
                  }}
                >
                  Yeni E-Ticaret Platformu Ekle
                </Button>
              </div>
            )}
          </Box>
        </Box>
      )}
    </>
  );
};

export default Dashboard;
